import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { InquiryRequest } from "../Services/authService";
import NormalAlert from "../../Alerts/NormalAlert";
import {
  Briefcase,
  UserPlus,
  CalendarCheck,
  FileCheck,
  Rocket,
  User,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle,
  Layers,
  Workflow,
  FileText,
  MessageSquareWarning,
  BadgeCheck,
  Database,
  Users,
  BarChart3,
  Building2,
  ShieldCheck,
  Search,
  LayoutDashboard,
  RefreshCcw,
  Loader,
} from "lucide-react";
import { motion } from "framer-motion";
import HRVector1 from "../../../Assets/HRVector1.png";
import HRVector2 from "../../../Assets/HRVector2.png";
import { getCaptchaService } from "../Services/ToolKitService";

const MarketingLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contactRef = useRef(null);

  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    contactNo: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    contactNo: "",
    message: "",
    captcha: "",
  });
  const [alert, setAlert] = useState({ type: "", message: "" });

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    getCaptcha();
  }, []);

  const validateForm = () => {
  let newErrors = {};

  if (!contactForm.name.trim()) {
    newErrors.name = "Name is required";
  }

  if (!contactForm.email.trim()) {
    newErrors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(contactForm.email)) {
    newErrors.email = "Invalid email format";
  }

  if (!contactForm.contactNo.trim()) {
    newErrors.contactNo = "Contact No is required";
  } else if (!/^[0-9]{10}$/.test(contactForm.contactNo)) {
    newErrors.contactNo = "Enter valid 10-digit number";
  }

  if (!contactForm.message.trim()) {
    newErrors.message = "Message is required";
  }

  if (!captchaInput.trim()) {
    newErrors.captcha = "Captcha is required";
  } else if (captchaInput !== captchaText) {
    newErrors.captcha = "Incorrect captcha";
  }

  setErrors({ ...newErrors });

  if (Object.keys(newErrors).length > 0) {
    if (newErrors.captcha) {
      getCaptcha();
      setCaptchaInput("");
    }
    return false;
  }

  return true;
};

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();

    if (!isValid) return;

    try {
      const res = await InquiryRequest(
        contactForm.name,
        contactForm.email,
        contactForm.contactNo,
        contactForm.message,
        captchaInput,
      );

      setAlert({
        type: "success",
        message: res.data.message,
      });

      setContactForm({
        name: "",
        email: "",
        contactNo: "",
        message: "",
      });

      setErrors({});
      getCaptcha();
      setCaptchaInput("");
    } catch (err) {
      setAlert({
        type: "fail",
        message: err?.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 80 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.1,
        ease: [0.25, 0.8, 0.25, 1],
      },
    },
  };

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.25,
        delayChildren: 0.2,
      },
    },
  };

  const baseUrl = "https://o4shrsetu.com";

  const features = [
    {
      title: "End-to-end job creation",
      description:
        "Create detailed requisitions with role category, department, location, qualifications, experience bands, openings, managers, JD upload, and interviewer sequence.",
      icon: Briefcase,
    },
    {
      title: "Draft and live job control",
      description:
        "Review jobs before release, publish when ready, update active jobs, and monitor candidate activity directly from live job views.",
      icon: Layers,
    },
    {
      title: "Structured candidate funnel",
      description:
        "Manage incoming applications, shortlisted candidates, interviews, assessments, selected candidates, offer released, hired, rejected, hold, backout, and BGV stages.",
      icon: Workflow,
    },
    {
      title: "Interview and assessment coordination",
      description:
        "Schedule rounds, assign interviewers, review interview details and feedback, and move candidates forward with better visibility.",
      icon: CalendarCheck,
    },
    {
      title: "Resume and profile review",
      description:
        "Open candidate details, view resumes, inspect remarks, and compare job-fit information before every decision.",
      icon: FileText,
    },
    {
      title: "Issue escalation",
      description:
        "Capture operational or platform issues directly from the dashboard through the built-in report issue workflow.",
      icon: MessageSquareWarning,
    },
  ];

  const whyFeatures = [
    {
      title: "Built for real recruitment flow",
      description:
        "HR Setu mirrors the actual hiring journey instead of forcing teams into generic task lists.",
      icon: BadgeCheck,
    },
    {
      title: "Single source of pipeline truth",
      description:
        "Jobs, candidate stages, interview progress, and recruiter-consultant collaboration stay connected.",
      icon: Database,
    },
    {
      title: "Faster team coordination",
      description:
        "Recruiters, consultants, interviewers, and admins each get focused workspaces for their role.",
      icon: Users,
    },
    {
      title: "Operational visibility",
      description:
        "Dashboards, stage-based views, filters, and detailed records reduce follow-up effort and manual tracking.",
      icon: BarChart3,
    },
  ];

  const faqs = [
    {
      question: "Who can use HR Setu?",
      answer:
        "HR Setu supports internal recruiter or employee users, consultants, interviewers, and admin users with role-specific access and workflows.",
    },
    {
      question: "Can recruiters and consultants work on the same openings?",
      answer:
        "Yes. Recruiters can publish and manage jobs while consultants can view open roles, submit profiles, and track candidate movement for their submissions.",
    },
    {
      question: "Does HR Setu support complete candidate lifecycle tracking?",
      answer:
        "Yes. The platform covers incoming applications through shortlist, interviews, assessments, selection, offer release, hiring, hold, rejection, backout, and background verification stages.",
    },
    {
      question: "How does candidate submission work?",
      answer:
        "Candidates are first verified using core details such as name, email, phone, PAN if available, and DOB, then a full profile with resume and hiring details can be submitted.",
    },
    {
      question: "Can the system help with issue reporting and administration?",
      answer:
        "Yes. Recruiters can raise issues from the dashboard, and admins can manage employees, consultants, and master data from dedicated admin areas.",
    },
  ];

  const getCaptcha = async () => {
    try {
      const response = await getCaptchaService();
      const data = response.data;

      setCaptchaImage(data.captchaImage);
      setCaptchaText(data.captchaText);
      setCaptchaInput("");
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error(err);
      setAlert({
        type: "error",
        message: "Failed to load captcha. Try again!",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>HR Setu | Recruitment Software & Hiring Platform</title>

        <meta
          name="description"
          content="HR Setu helps automate hiring, manage candidates, and streamline recruitment workflows."
        />

        <meta
          name="keywords"
          content="HR software, recruitment platform, ATS, hiring automation"
        />

        <meta name="robots" content="index, follow" />

        {/* Dynamic Canonical */}
        <link
          rel="canonical"
          href={`${baseUrl}${
            location.pathname.endsWith("/")
              ? location.pathname
              : location.pathname + "/"
          }`}
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="HR Setu - Smart Recruitment Platform"
        />
        <meta
          property="og:description"
          content="Automate hiring and manage candidates easily."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-slate-50 text-slate-900">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-md">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
            <div className="flex items-center gap-3">
              <img
                src={process.env.PUBLIC_URL + "/HRSetu-final-logo.svg"}
                alt="HR Setu"
                className="h-14 w-auto me-4"
              />
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-sky-600">
                  HR Setu
                </p>
                <p className="text-sm text-slate-600">
                  Modern HR automation for every team
                </p>
              </div>
            </div>

            <nav className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
              <button
                className="rounded-full bg-sky-600 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-300/30 transition hover:bg-sky-500"
                onClick={scrollToContact}
              >
                Free Demo
              </button>
            </nav>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-14 lg:py-20">
          <motion.section
            variants={container}
            initial="hidden"
            animate="show"
            className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-start"
          >
            <motion.div variants={fadeUp} className="space-y-8">
              <p className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-4 py-2 text-sm font-semibold uppercase tracking-[0.35em] text-sky-700 shadow-sm">
                Your trusted HR partner
              </p>
              <h1 className="text-4xl font-semibold leading-tight text-slate-950 sm:text-4xl lg:text-5xl">
                Smart Recruitment. Faster Hiring. Better Decisions.
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-slate-600 sm:text-xl">
                HR Setu is a centralized recruitment platform that automates the
                entire hiring workflow — from job creation to candidate
                onboarding.{" "}
              </p>

              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { value: "90+", label: "Integrations" },
                  { value: "100+", label: "Clients onboarded" },
                  { value: "4.9/5", label: "Average rating" },
                ].map((item) => (
                  <motion.div
                    variants={fadeUp}
                    key={item.label}
                    className="rounded-3xl border bg-white p-6 text-center shadow-sm"
                  >
                    <p className="text-3xl font-bold text-slate-950">
                      {item.value}
                    </p>
                    <p className="mt-2 text-sm text-slate-500">{item.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/70"
            >
              <div className="space-y-6">
                {/* Heading */}
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-sky-600">
                    HR Setu
                  </p>
                  <h2 className="mt-3 text-3xl font-bold text-slate-950">
                    A smarter HR platform for every stage of the employee
                    lifecycle
                  </h2>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    "Centralized recruitment management",
                    "Automated hiring workflows",
                    "Real-time analytics & reporting",
                    "Seamless team collaboration",
                    "Smart duplicate detection",
                    "Interview scheduling & tracking",
                  ].map((item) => (
                    <motion.div
                      key={item}
                      variants={fadeUp}
                      whileHover={{ y: -3 }}
                      className="flex items-start gap-3 bg-slate-50 p-4 rounded-2xl border hover:bg-white hover:shadow-md transition"
                    >
                      <CheckCircle size={18} className="text-sky-600 mt-1" />
                      <p className="text-sm text-slate-700">{item}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-24 bg-white rounded-[2rem] p-10"
          >
            <div className="grid gap-12 lg:grid-cols-2 items-center">
              {/* IMAGE */}
              <motion.div variants={fadeUp} className="flex justify-center">
                <img
                  src={HRVector1}
                  alt="HR dashboard showing recruitment and candidate management analytics"
                  className="w-full max-w-[400px] rounded-3xl"
                />
              </motion.div>

              {/* TEXT */}
              <motion.div variants={fadeUp} className="space-y-6">
                <h2 className="text-4xl font-bold text-slate-950 leading-tight">
                  Manage Everything from One Dashboard
                </h2>

                <p className="text-slate-600 text-lg leading-8">
                  Get complete visibility into your hiring pipeline with a
                  centralized dashboard that tracks jobs, candidates, and
                  interviews in real time.
                </p>

                <div className="flex gap-4">
                  <button
                    onClick={scrollToContact}
                    className="rounded-full bg-sky-600 px-6 py-3 text-white font-semibold shadow hover:bg-sky-500 transition"
                  >
                    Explore Portal
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-20 rounded-[2rem] bg-slate-100 p-10"
          >
            {/* TOP HEADING */}
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-xs uppercase tracking-[0.35em] text-sky-600">
                CORE FEATURES
              </p>

              <h2 className="mt-4 text-3xl font-bold text-slate-950 leading-snug">
                Everything your hiring team needs to manage demand, submissions,
                and decisions.
              </h2>
            </div>

            {/* CARDS */}
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((item) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    variants={fadeUp}
                    whileHover={{ y: -6 }}
                    key={item.title}
                    className="rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:shadow-lg"
                  >
                    {/* ICON */}
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                      <Icon size={22} />
                    </div>

                    {/* TITLE */}
                    <h3 className="mt-5 text-lg font-semibold text-slate-950">
                      {item.title}
                    </h3>

                    {/* DESCRIPTION */}
                    <p className="mt-3 text-sm leading-7 text-slate-600">
                      {item.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-24 bg-slate-100 rounded-[2rem] p-10"
          >
            {/* TOP HEADING */}
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.35em] text-sky-600">
                WHY CHOOSE US
              </p>
            </div>

            {/* CONTENT */}
            <div className="grid gap-12 lg:grid-cols-2 items-center">
              <div className="grid gap-6 grid-cols-1 lg:grid-cols-1">
                {whyFeatures.map((item) => {
                  const Icon = item.icon;

                  return (
                    <motion.div
                      key={item.title}
                      variants={fadeUp}
                      whileHover={{ y: -5 }}
                      className="rounded-3xl border border-slate-200 bg-white p-6 hover:shadow-lg transition"
                    >
                      {/* ICON */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full border border-blue-200 text-blue-600">
                          <Icon size={18} />
                        </div>

                        <h3 className="font-semibold text-slate-900">
                          {item.title}
                        </h3>
                      </div>
                      {/* DESCRIPTION */}
                      <p className="mt-2 text-sm text-slate-600 leading-6">
                        {item.description}
                      </p>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div
                variants={fadeUp}
                className="flex justify-center"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1 }}
              >
                <img
                  src={HRVector2}
                  alt="Recruitment workflow"
                  className="w-full max-w-[450px]"
                />
              </motion.div>
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-24 text-center"
          >
            <h2 className="text-3xl font-bold text-slate-950">
              Why Hiring is Still Broken
            </h2>
            <p className="text-slate-500 mt-3">
              Traditional recruitment processes are slow, manual, and
              inefficient
            </p>

            <div className="grid md:grid-cols-4 gap-6 mt-10">
              {[
                { value: "42 Days", label: "Average Hiring Delay" },
                { value: "60%", label: "Manual Tracking" },
                { value: "3x", label: "Communication Gaps" },
                { value: "78%", label: "Lack of Visibility" },
              ].map((item) => (
                <motion.div
                  variants={fadeUp}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white p-8 rounded-3xl shadow hover:shadow-xl transition"
                >
                  <p className="text-3xl font-bold text-sky-600">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-24"
          >
            <h2 className="text-3xl font-bold text-center mb-14">
              Who Uses HR Setu?
            </h2>

            <div className="grid lg:grid-cols-2 gap-10">
              {/* LEFT BLUE CARD */}
              <motion.div
                variants={fadeUp}
                className="rounded-[2.5rem] p-10 text-white bg-gradient-to-br from-[#1e4fa3] to-[#1b3f8b] shadow-2xl"
              >
                <p className="text-xs uppercase tracking-[0.3em] bg-white/20 inline-block px-4 py-1.5 rounded-full">
                  Consultant Functionality
                </p>

                <h3 className="mt-6 text-2xl font-bold leading-snug">
                  Strong recruiter and consultant collaboration without losing
                  control.
                </h3>

                <div className="mt-8 space-y-4">
                  {[
                    "Browse all open jobs with filters and job detail views",
                    "Submit candidates against active jobs with verification before profile creation",
                    "Track profile shared records and candidate progress in one place",
                    "Monitor shortlisted, interview scheduled, and selected candidates",
                    "Share resumes and candidate details with a structured submission flow",
                  ].map((item) => (
                    <div className="flex items-start gap-3 bg-white/10 p-4 rounded-xl">
                      <CheckCircle size={18} className="text-white mt-0.5" />
                      <p className="text-sm leading-6">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT SIDE */}
              <div className="space-y-6">
                {/* TOP CARD */}
                <motion.div
                  variants={fadeUp}
                  className="rounded-[2.5rem] bg-white p-10 shadow-xl border border-slate-200"
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-sky-600 bg-sky-100 inline-block px-4 py-1.5 rounded-full">
                    Admin and Platform Support
                  </p>

                  <h3 className="mt-5 text-xl font-bold text-slate-950 leading-snug">
                    Administrative controls that help the process stay clean and
                    scalable.
                  </h3>

                  <div className="mt-6 space-y-3">
                    {[
                      {
                        text: "Add employees and consultants",
                        icon: Building2,
                      },
                      {
                        text: "Maintain master data for operational consistency",
                        icon: Database,
                      },
                      {
                        text: "Support role-based access across recruiter, consultant, interviewer, and admin journeys",
                        icon: ShieldCheck,
                      },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                          <Icon size={18} className="text-sky-600" />
                          <p className="text-sm text-slate-700">{item.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* BOTTOM DARK CARD */}
                <motion.div
                  variants={fadeUp}
                  className="rounded-[2.5rem] bg-white p-10 shadow-xl border border-slate-200"
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-sky-600 bg-sky-100 inline-block px-4 py-1.5 rounded-full">
                    INCLUDED OPERATIONAL CAPABILITIES
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-4">
                    {[
                      {
                        text: "Search, filters, pagination, and action menus",
                        icon: Search,
                      },
                      {
                        text: "Role-based login journeys and dashboard access",
                        icon: LayoutDashboard,
                      },
                      {
                        text: "Candidate verification before submission",
                        icon: ShieldCheck,
                      },
                      {
                        text: "Resume upload with structured profile data",
                        icon: FileText,
                      },
                      {
                        text: "Job details and JD visibility for decision makers",
                        icon: Briefcase,
                      },
                      {
                        text: "Interviewer coordination and stage movement support",
                        icon: Users,
                      },
                    ].map((item) => {
                      const Icon = item.icon;

                      return (
                        <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                          <Icon size={18} className="text-sky-600 mt-1" />
                          <p className="text-sm text-slate-700 leading-6">
                            {item.text}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-24 text-center"
          >
            <h2 className="text-3xl font-bold text-slate-950 mb-3">
              <span className="text-sky-600">Simple</span> Hiring Workflow
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto mb-12">
              From job creation to onboarding — streamline every step with HR
              Setu
            </p>

            <motion.div
              variants={container}
              className="grid md:grid-cols-5 gap-6 relative"
            >
              {[
                { label: "Job Creation", icon: Briefcase },
                { label: "Candidate Submission", icon: UserPlus },
                { label: "Interview", icon: CalendarCheck },
                { label: "Offer", icon: FileCheck },
                { label: "Hiring", icon: Rocket },
              ].map((step, i) => {
                const Icon = step.icon;

                return (
                  <motion.div
                    variants={fadeUp}
                    whileHover={{ y: -8, scale: 1.03 }}
                    key={step.label}
                    className="relative group bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    {/* Icon */}
                    <div className="flex justify-center mb-4">
                      <div className="bg-sky-100 text-sky-600 p-3 rounded-2xl group-hover:bg-sky-600 group-hover:text-white transition">
                        <Icon size={22} />
                      </div>
                    </div>

                    {/* Label */}
                    <p className="font-semibold text-slate-800">{step.label}</p>

                    {/* Connector line (except last) */}
                    {i !== 4 && (
                      <div className="hidden md:block absolute top-1/2 right-[-60px] w-[60px] h-[2px] bg-gradient-to-r from-sky-300 to-sky-100" />
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-24"
          >
            <h2 className="text-3xl font-bold text-center mb-10">
              Real-Time Insights
            </h2>

            <div className="grid md:grid-cols-4 gap-6 text-center">
              {[
                { value: "124", label: "Jobs" },
                { value: "2,847", label: "Candidates" },
                { value: "56", label: "Interviews" },
                { value: "312", label: "Hired" },
              ].map((item) => (
                <div
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  className="bg-gradient-to-br from-sky-50 to-white p-6 rounded-3xl shadow hover:shadow-xl"
                >
                  <p className="text-3xl font-bold text-sky-600">
                    {item.value}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-24 bg-slate-100 rounded-[2rem] p-10"
          >
            {/* HEADING */}
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.35em] text-sky-600">
                FAQs
              </p>

              <h2 className="mt-4 text-3xl font-bold text-slate-950">
                Answers to common questions about HR Setu.
              </h2>
            </div>

            {/* FAQ LIST */}
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 cursor-pointer transition hover:shadow-md"
                >
                  <summary className="flex justify-between items-center font-medium text-slate-900 list-none">
                    {faq.question}
                    <span className="text-sky-600 group-open:rotate-180 transition">
                      ▼
                    </span>
                  </summary>

                  <p className="mt-3 text-sm text-slate-600 leading-6">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </motion.section>

          <motion.section
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            ref={contactRef}
            className="mt-24 rounded-[2rem] bg-slate-100 p-10 shadow-lg shadow-slate-200"
          >
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-start">
              <motion.div variants={fadeUp}>
                <p className="text-xs uppercase tracking-[0.35em] text-sky-600">
                  Free Demo
                </p>
                <h2 className="mt-4 text-4xl font-bold text-slate-950">
                  Ready to get started with HR Setu?
                </h2>
                <p className="mt-4 max-w-2xl text-slate-600 leading-8">
                  Create your first job, submit candidates, and experience a
                  smarter hiring process with HR Setu.
                </p>
              </motion.div>

              <motion.div
                variants={fadeUp}
                className="space-y-6 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/70"
              >
                {/* Email*/}
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-slate-500">
                    Email
                  </p>
                  <p className="mt-2 text-slate-900 font-medium">
                    sales@vrsamadhantech.com
                  </p>
                </div>

                {/* Phone & Location */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] text-slate-500">
                      Phone
                    </p>
                    <p className="mt-2 text-slate-900 font-medium">
                      +91 88506 71139
                    </p>
                  </div>

                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] text-slate-500">
                      Location
                    </p>
                    <p className="mt-2 text-slate-900 font-medium">
                      Thane, Maharashtra, India
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.form
              variants={fadeUp}
              onSubmit={handleContactSubmit}
              className="mt-10 grid gap-6 lg:grid-cols-[1.3fr_0.7fr]"
            >
              <div className="grid gap-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <label className="block">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <User size={16} className="text-sky-600" />
                      <span>Name / Organization Name</span>
                      <span className="text-red-500">*</span>
                    </div>

                    <input
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactChange}
                      placeholder="Your name"
                      className="mt-2 w-full rounded-3xl border border-slate-200 px-4 py-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    />

                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </label>

                  <label className="block">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail size={16} className="text-sky-600" />
                      <span>Email</span>
                      <span className="text-red-500">*</span>
                    </div>

                    <input
                      name="email"
                      type="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      placeholder="you@example.com"
                      className="mt-2 w-full rounded-3xl border border-slate-200 px-4 py-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    />

                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.email}
                      </p>
                    )}
                  </label>
                </div>

                <label className="block">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone size={16} className="text-sky-600" />
                    <span>Contact No</span>
                    <span className="text-red-500">*</span>
                  </div>

                  <input
                    name="contactNo"
                    value={contactForm.contactNo}
                    onChange={handleContactChange}
                    placeholder="Enter contact number"
                    className="mt-2 w-full rounded-3xl border border-slate-200 px-4 py-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  />

                  {errors.contactNo && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.contactNo}
                    </p>
                  )}
                </label>

                <label className="block">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MessageSquare size={16} className="text-sky-600" />
                    <span>Message / Enquiry</span>
                    <span className="text-red-500">*</span>
                  </div>

                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    placeholder="Share your query or message here"
                    className="mt-2 h-40 w-full rounded-3xl border border-slate-200 px-4 py-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  />

                  {errors.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.message}
                    </p>
                  )}
                </label>

                <label className="block">
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                    <span>Captcha</span>
                    <span className="text-red-500">*</span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Captcha Image */}
                    <div
                      className="cursor-pointer rounded-md flex items-center justify-center shadow-sm"
                      onClick={getCaptcha}
                    >
                      {captchaImage ? (
                        <img
                          src={captchaImage}
                          alt="Captcha"
                          className="h-[45px] w-auto select-none"
                          draggable="false"
                        />
                      ) : (
                        <div className="h-[45px] w-[120px] flex justify-center items-center">
                          <Loader
                            className="animate-spin text-gray-500"
                            size={22}
                          />
                        </div>
                      )}
                    </div>

                    {/* Refresh Icon */}
                    <button
                      type="button"
                      onClick={getCaptcha}
                      title="Refresh Captcha"
                      className="text-[#0061A4] hover:text-[#004a80] transition p-1"
                    >
                      <RefreshCcw size={22} />
                    </button>

                    {/* Input */}
                    <input
                      type="text"
                      placeholder="Enter code"
                      value={captchaInput}
                      onChange={(e) => {
                        setCaptchaInput(e.target.value);
                        setErrors((prev) => ({ ...prev, captcha: "" }));
                      }}
                      className="w-[200px] pl-3 pr-2 py-2 border border-gray-300 rounded-md
      focus:outline-none focus:ring-2 focus:ring-[#007BFF] text-sm"
                    />
                  </div>

                  {errors.captcha && (
                    <p className="text-red-500 text-xs mt-2">
                      {errors.captcha}
                    </p>
                  )}
                </label>
              </div>

              <div className="flex flex-col justify-between rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/70">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-slate-500">
                    Need guidance?
                  </p>
                  <p className="mt-4 text-slate-600 leading-7">
                    Tell us your team size, hiring goals, or pain points and
                    we’ll share a tailored HR Setu experience.
                  </p>
                </div>
                <button
                  type="submit"
                  className="mt-6 rounded-full bg-sky-600 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-300/30 hover:bg-sky-500 transition"
                >
                  Send Message
                </button>
              </div>
            </motion.form>
          </motion.section>
        </main>

        <footer className="border-t border-slate-200 bg-white py-8">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm text-slate-600">
                Streamline your recruitment with HR Setu — faster, smarter, and
                more efficient hiring.
              </p>
              <p className="mt-3 text-xs text-slate-500">
                Designed and Developed by:{" "}
                <span className="text-blue-500">VRSamadhan Tech Solution.</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <button
                className="rounded-full bg-sky-600 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-300/30 transition hover:bg-sky-500"
                onClick={scrollToContact}
              >
                Free Demo
              </button>
            </div>
          </div>
        </footer>
      </div>
      {alert?.message && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ type: "", message: "" })}
        />
      )}
    </>
  );
};

export default MarketingLanding;
